import React, { useEffect, useState, useCallback } from 'react';
import { Add, ArrowBack, Edit, Delete, CallMerge, ChevronRight, ExpandMore } from "@mui/icons-material";
import api from '../../services/api';
import styles from '../../assets/styles/LayoutShared.module.css';
import LocalForm from './LocalForm';
import LocalMesclarModal from './LocalMesclarModal';

interface NoArvore {
  local: any;
  filhos: NoArvore[];
}

function montarArvore(locais: any[]): NoArvore[] {
  const porUid = new Map<string, NoArvore>();
  locais.forEach((l) => porUid.set(l.uid_pesca_local, { local: l, filhos: [] }));

  const raizes: NoArvore[] = [];
  locais.forEach((l) => {
    const no = porUid.get(l.uid_pesca_local)!;
    if (l.uid_pesca_local_pai && porUid.has(l.uid_pesca_local_pai)) {
      porUid.get(l.uid_pesca_local_pai)!.filhos.push(no);
    } else {
      raizes.push(no);
    }
  });

  const porNome = (a: NoArvore, b: NoArvore) => a.local.nome.localeCompare(b.local.nome);
  const ordenar = (nos: NoArvore[]) => {
    nos.sort(porNome);
    nos.forEach((n) => ordenar(n.filhos));
  };
  ordenar(raizes);
  return raizes;
}

const LocaisList = () => {
  const [locais, setLocais] = useState<any[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [uidEditando, setUidEditando] = useState<string | undefined>(undefined);
  const [uidPaiParaNovo, setUidPaiParaNovo] = useState<string | undefined>(undefined);
  const [mesclandoLocal, setMesclandoLocal] = useState<any>(null);

  const fetchLocais = useCallback(() => {
    setCarregando(true);
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    api.get(`/pesca/locais?${params.toString()}`)
      .then((res) => setLocais(res.data.data))
      .finally(() => setCarregando(false));
  }, [search]);

  useEffect(() => { fetchLocais(); }, [fetchLocais]);

  const arvore = montarArvore(locais);

  const toggleExpandido = (uid: string) => {
    setExpandidos((prev) => {
      const novo = new Set(prev);
      if (novo.has(uid)) novo.delete(uid); else novo.add(uid);
      return novo;
    });
  };

  const handleNovo = (uidPai?: string) => {
    setUidEditando(undefined);
    setUidPaiParaNovo(uidPai);
    setFormOpen(true);
  };

  const handleEditar = (uid: string) => {
    setUidEditando(uid);
    setUidPaiParaNovo(undefined);
    setFormOpen(true);
  };

  const handleExcluir = async (local: any) => {
    if (!window.confirm(`Remover o local "${local.nome}"?`)) return;
    try {
      await api.delete(`/pesca/locais/${local.uid_pesca_local}`);
      fetchLocais();
    } catch (err: any) {
      alert(err?.response?.data?.message || 'Erro ao remover local.');
    }
  };

  const handleSalvo = () => {
    setFormOpen(false);
    fetchLocais();
  };

  const renderNo = (no: NoArvore, profundidade: number): React.ReactNode => {
    const temFilhos = no.filhos.length > 0;
    const aberto = expandidos.has(no.local.uid_pesca_local);
    return (
      <React.Fragment key={no.local.uid_pesca_local}>
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '8px 10px', paddingLeft: `${10 + profundidade * 22}px`,
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          <span
            style={{ width: '20px', display: 'inline-flex', cursor: temFilhos ? 'pointer' : 'default', color: '#999' }}
            onClick={() => temFilhos && toggleExpandido(no.local.uid_pesca_local)}
          >
            {temFilhos ? (aberto ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />) : null}
          </span>

          <span style={{ ...tipoBadgeStyle, ...tipoCores[no.local.tipo as string] }}>{TIPO_LABEL[no.local.tipo] || no.local.tipo}</span>
          <strong style={{ fontSize: '0.9rem', color: '#333' }}>{no.local.nome}</strong>
          {no.local.municipio && <span style={{ fontSize: '0.78rem', color: '#999' }}>— {no.local.municipio}{no.local.uf ? `/${no.local.uf}` : ''}</span>}

          <div style={{ marginLeft: 'auto', display: 'flex', gap: '10px' }}>
            <Add fontSize="small" titleAccess="Adicionar sub-local" style={{ cursor: 'pointer', color: '#00b37e' }} onClick={() => handleNovo(no.local.uid_pesca_local)} />
            <Edit fontSize="small" titleAccess="Editar" style={{ cursor: 'pointer', color: '#03c9fe' }} onClick={() => handleEditar(no.local.uid_pesca_local)} />
            <CallMerge fontSize="small" titleAccess="Mesclar com outro local" style={{ cursor: 'pointer', color: '#886fd1' }} onClick={() => setMesclandoLocal(no.local)} />
            <Delete fontSize="small" titleAccess="Remover" style={{ cursor: 'pointer', color: '#f75a68' }} onClick={() => handleExcluir(no.local)} />
          </div>
        </div>
        {temFilhos && aberto && no.filhos.map((f) => renderNo(f, profundidade + 1))}
      </React.Fragment>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerTitle}>
        <h1 className={styles.greenTitle}>Locais de Pesca</h1>

        <div style={{ position: 'relative', flex: 1, maxWidth: '360px', margin: '0 20px' }}>
          <input
            type="text"
            placeholder="Buscar por nome..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: '100%', padding: '10px 14px', borderRadius: '20px', border: '1px solid #ddd', outline: 'none' }}
          />
        </div>

        <div className={styles.actionsHeader}>
          <p style={{ alignSelf: 'center', fontSize: '0.9rem', color: '#666', marginRight: '10px' }}>
            Qtd: <span style={{ fontWeight: 'bold', color: 'var(--green-700)' }}>{locais.length}</span>
          </p>
          <button className={styles.blueBtn} onClick={() => handleNovo(undefined)} title="Adicionar Local Raiz">
            <Add />
          </button>
          <button className={styles.blueBtn} onClick={() => window.history.back()} title="Voltar">
            <ArrowBack />
          </button>
        </div>
      </div>

      <div className={styles.tableWrapper} style={{ padding: 0 }}>
        {carregando ? (
          <p style={{ padding: '20px', textAlign: 'center', color: '#999' }}>Carregando...</p>
        ) : arvore.length === 0 ? (
          <p style={{ padding: '20px', textAlign: 'center', color: '#999' }}>Nenhum local cadastrado.</p>
        ) : (
          arvore.map((no) => renderNo(no, 0))
        )}
      </div>

      {formOpen && (
        <LocalForm
          uidEditando={uidEditando}
          uidPaiInicial={uidPaiParaNovo}
          todosOsLocais={locais}
          onClose={() => setFormOpen(false)}
          onSaved={handleSalvo}
        />
      )}

      {mesclandoLocal && (
        <LocalMesclarModal
          local={mesclandoLocal}
          todosOsLocais={locais}
          onClose={() => setMesclandoLocal(null)}
          onMesclado={() => { setMesclandoLocal(null); fetchLocais(); }}
        />
      )}
    </div>
  );
};

const TIPO_LABEL: Record<string, string> = {
  bacia: 'Bacia', rio: 'Rio', represa: 'Represa',
  trecho_municipio: 'Trecho', area_pesca: 'Área', outro: 'Outro',
};
const tipoCores: Record<string, React.CSSProperties> = {
  bacia: { background: '#e3f2fd', color: '#1565c0' },
  rio: { background: '#e0f7fa', color: '#00838f' },
  represa: { background: '#ede7f6', color: '#5e35b1' },
  trecho_municipio: { background: '#fff3e0', color: '#e65100' },
  area_pesca: { background: '#e8f5e9', color: '#2e7d32' },
  outro: { background: '#f5f5f5', color: '#666' },
};
const tipoBadgeStyle: React.CSSProperties = {
  fontSize: '0.68rem', fontWeight: 700, padding: '2px 8px', borderRadius: '10px', textTransform: 'uppercase', letterSpacing: '0.02em',
};

export default LocaisList;
